import { useId, useState, type ReactNode } from "react";
import {
  Banknote,
  Bike,
  Check,
  CreditCard,
  ListChecks,
  Receipt,
  Smartphone,
  X,
  type LucideIcon,
} from "lucide-react";
import { ModalSheet, NumberInput } from "@a2z/ui";
import { useTranslate } from "@/lib/i18n";
import { commissionBaseLabel, settleTableTitle } from "@/lib/invoice/place";
import { countedKey } from "@/lib/i18n/plural";
import { settlementMethodLabel } from "@/lib/settlementMethodLabel";
import { DoodleBadge } from "@/shared/doodles/DoodleBadge";
import { useLocalName } from "@/shared/localName";
import { useMoney } from "@/shared/ui";
import {
  UnsavedChangesPrompt,
  useDiscardGuard,
} from "@/shared/UnsavedChangesPrompt";
import { DrawerWarning } from "./DrawerWarning";
import { TenderReferenceField } from "./TenderReferenceField";
import { hasPendingTenderReference } from "@/modules/pos/lib/tenderReference";
import { markablePlates, partyShares } from "@/modules/pos/lib/partyShares";
import { isRungOnTheBill } from "@/modules/pos/lib/groupPlates";
import {
  amountTaken,
  effectiveSettleMethod,
  initialSettleMethod,
  nextPartyLabel,
  paidPlateIds,
  runSettle,
  selectedCount,
  selectedPlateIds,
  selectionAmount,
  settleMethodOptions,
  settlePayments,
  settleRemaining,
  settleRows,
  type SendSettleCommand,
  type SettleMode,
  type SettleSelection,
} from "@/modules/pos/lib/settleSheet";
import type {
  CommissionBase, Invoice, SettlementMethod } from "@/lib/api/types";

const METHOD_ICONS: Record<SettlementMethod, LucideIcon> = {
  CARD: CreditCard,
  CASH: Banknote,
  INSTAPAY: Smartphone,
  AGGREGATOR: Bike,
  WRITE_OFF: X,
};

/* The design draws every icon in this sheet with one ink and one weight. */
const ICON_INK = "rgb(var(--px-ink-rgb, 55 53 47) / 0.6)";

/**
 * THE SETTLE WINDOW — PAPER only (the 2026-10 redesign's Settle modal, founder
 * 2026-10-03: "pay in full and by item — no split evenly — and keep InstaPay").
 *
 * Two modes, and both are commands the till already sends (`settleSheet.ts`
 * says which, and why each is the honest mapping):
 *
 *  · **Full bill** — the whole remainder in one tender. `settleInvoice` on a bill
 *    nobody has paid toward, `settlePartial` for "the rest" on one somebody has.
 *    Its amount is editable: less than what is owed is a PART payment
 *    (`settlePartial` with that amount — B3's split tender, "the guest pays 200
 *    now", short cash), and the window stays open on the new remainder.
 *  · **By item** — the guest's plates, picked with quantities, become a party
 *    and that party is settled; the server derives the figure, and the sheet
 *    quotes it from the same allocation.
 *
 * A settle needs a CLOSED bill, so on an open one the Pay tap closes it first
 * (`closeInvoice`, on the version this screen observed). The design's own line
 * — "unpaid items stay on the table so guests can keep ordering" — is not true
 * here, so the sheet says what IS: paying closes the bill to new orders, and
 * what is not paid stays on it.
 *
 * Tenders: Card and Cash (the design's), InstaPay always LISTED and taken once a
 * transfer is confirmed (D44), and the platform on a platform's order (F2).
 * The walk-out write-off is NOT a tender: it stays the separate two-tap act on
 * the void rung (D31), behind its own quiet link.
 *
 * Built on `ModalSheet` rather than `Modal`: the redesign's sheet has its own
 * head (badge, title, the remaining figure) and no footer bar. Its Pay sits in
 * a non-scrolling foot BELOW the item list's own scroller, so the primary
 * action can never be pushed under the fold — D10's rule, kept without
 * `hideFooter`.
 */
export function PaperSettleSheet({
  invoice,
  placeName,
  aggregator,
  transferConfirmed,
  canPayByItem,
  canWriteOff,
  keepOpen = false,
  keepOpenNote,
  warning,
  warningActionLabel,
  onOpenDrawer,
  send,
  onClose,
  initialPlateIds,
  initialAmount,
  initialMethod,
  dinerPaymentId,
}: {
  invoice: Invoice;
  /** What the bill is called on the floor — the table, or the channel. */
  placeName: string;
  /** F2 — the platform that brought this order, or `null`. */
  aggregator: { name: string; commissionPercent: number; commissionBase?: CommissionBase } | null;
  /** D44 — a diner's transfer for this bill has been confirmed by staff. */
  transferConfirmed: boolean;
  /**
   * `RESTAURANT_INVOICE_APPEND` — "by item" marks the guest's plates as a party
   * (`assignParty`) before taking their share. Without it there is one mode and
   * no switch.
   */
  canPayByItem: boolean;
  /** `RESTAURANT_INVOICE_VOID` — the walk-out write-off's rung (D31). */
  canWriteOff: boolean;
  /**
   * D126 — the open bill cannot be closed yet (a basket not sent, a plate
   * waiting at the kitchen), so Pay takes the money WITHOUT closing it: the
   * kitchen keeps cooking and the bill is closed later. It used to refuse Pay
   * with a sentence here (D22's).
   */
  keepOpen?: boolean;
  /**
   * T6 / D173 — under the keep-open line, WHY the bill stays open and the one
   * tap that clears it (the page's `CloseBlockedNote`, surface "settle"):
   * Settle is never greyed here (D126), so this is where the PAPER till says
   * what its Close would have said. Drawn only while the bill is OPEN.
   */
  keepOpenNote?: ReactNode;
  /** The D25 drawer warning, already branched on authority by the page. */
  warning?: string;
  warningActionLabel?: string;
  onOpenDrawer?: () => void;
  send: SendSettleCommand;
  onClose: () => void;
  /**
   * Plates ticked on the bill before Settle (founder, 2026-10-07): the sheet
   * opens in "by item" with them picked. Ignored without `canPayByItem`.
   */
  initialPlateIds?: readonly string[];
  /**
   * D133 — a diner's even share: the full-bill amount opens on it (a part
   * payment), instead of on everything owed. Ignored when it is not below what
   * is owed — then it IS the rest.
   */
  initialAmount?: number;
  /** D133 — the method the diner said they would pay by, still clamped to
      what this sheet offers (InstaPay only on a confirmed transfer). */
  initialMethod?: SettlementMethod;
  /**
   * D133 fix — the diner's claim this window was opened from ("Settle this
   * share"). Sent with the FIRST tender only — the server refuses a second
   * settle naming it — so a part payment followed by "the rest" from the same
   * window does not trip over its own claim. Whether the claim is confirmed is
   * the server's call: only when that tender was the claim unchanged.
   */
  dinerPaymentId?: string;
}) {
  const t = useTranslate();
  const money = useMoney();
  const localName = useLocalName();
  const titleId = useId();

  /* The newest bill this sheet has seen — the page's read, or what the last
     command returned if that is fresher. */
  const [returned, setReturned] = useState<Invoice | null>(null);
  const bill =
    returned && returned.id === invoice.id && returned.version > invoice.version
      ? returned
      : invoice;

  const startPicked = canPayByItem && (initialPlateIds?.length ?? 0) > 0;
  const [mode, setMode] = useState<SettleMode>(startPicked ? "items" : "full");
  const hasPlatform = aggregator !== null;
  const [chosen, setChosen] = useState<SettlementMethod>(
    () =>
      initialMethod ?? initialSettleMethod({ hasPlatform, transferConfirmed }),
  );
  const [selection, setSelection] = useState<SettleSelection>(() =>
    startPicked ? selectionForPlates(settleRows(invoice), initialPlateIds ?? []) : {},
  );
  /* The full-bill amount as typed — `null` follows what is owed, so after a
     part payment the field shows the NEW remainder without being cleared. */
  const [amountText, setAmountText] = useState<string | null>(() =>
    !startPicked &&
    typeof initialAmount === "number" &&
    initialAmount > 0 &&
    initialAmount < settleRemaining(invoice)
      ? initialAmount.toFixed(2)
      : null,
  );
  const [pending, setPending] = useState(false);
  /* The claim, until a tender from this window has taken it. */
  const [claimId, setClaimId] = useState<string | undefined>(dinerPaymentId);
  const [instapayHint, setInstapayHint] = useState(false);
  /* D163 — «مرجع العملية», optional; sent only with CARD/INSTAPAY. */
  const [referenceText, setReferenceText] = useState("");
  const [writeOffAsked, setWriteOffAsked] = useState(false);
  const [last, setLast] = useState<{
    amount: number;
    method: SettlementMethod;
    label: string;
    left: number;
    change: number;
  } | null>(null);

  const itemsMode = canPayByItem && mode === "items";
  const options = settleMethodOptions({
    mode: itemsMode ? "items" : "full",
    hasPlatform,
    transferConfirmed,
    tendered: bill.tenderedTotal,
  });
  const method = effectiveSettleMethod(chosen, options);

  const remaining = settleRemaining(bill);
  const isWrittenOff = bill.settlementMethod === "WRITE_OFF";
  const isDone = bill.status === "SETTLED" && !isWrittenOff;

  const rows = settleRows(bill, money);
  const paid = paidPlateIds(bill);
  const unpaidCount = markablePlates(bill).filter(
    (item) => !paid.has(item.id),
  ).length;
  /* A party that has put SOME of its share in: its plates are left off the
     list (`settleRows`), and the sheet says why rather than letting them
     vanish. */
  const partPaid = partyShares(bill).some(
    (share) => share.paid > 0 && !share.isSettled && share.itemCount > 0,
  );

  const pickedCount = selectedCount(rows, selection);
  const pickedAmount = selectionAmount(rows, selection);

  /*
   * THE FULL-BILL AMOUNT IS EDITABLE (B3 on this look, 2026-10-03 audit): it
   * opens on what is owed, and anything less is a PART payment — "the guest
   * pays 200 now", card for part and cash for the rest, or the cash handed
   * over falling short. The rules are `PartialSettleSheet`'s: a card or a
   * transfer is never charged more than is owed, cash may be (the change is
   * shown), and the platform takes a whole bill only, so it has no field.
   */
  const round2 = (value: number) => Math.round(value * 100) / 100;
  const hasAmountField = !itemsMode && method !== "AGGREGATOR" && remaining > 0;
  const amountValue = amountText === null ? remaining : Number(amountText);
  const amountValid =
    amountText === null ||
    (amountText.trim() !== "" && Number.isFinite(amountValue) && amountValue > 0);
  const overCharging =
    hasAmountField && method !== "CASH" && amountValid && round2(amountValue) > remaining;
  const takesPart =
    hasAmountField && amountValid && round2(amountValue) < remaining;
  const change =
    hasAmountField && method === "CASH" && amountValid && round2(amountValue) > remaining
      ? round2(amountValue - remaining)
      : 0;

  const payAmount = itemsMode ? pickedAmount : remaining;
  /*
   * D126 — an OPEN bill already paid in full (it was paid while the kitchen
   * cooked). Nothing is owed, so Pay has nothing to take; once nothing is
   * waiting any more the one act left is the close, which the server lands on
   * SETTLED. While the kitchen still has food, the sheet just says so.
   */
  const paidOpen = bill.status === "OPEN" && remaining <= 0 && bill.tenderedTotal > 0;
  const closePaid = paidOpen && !keepOpen;
  /*
   * An ALL-COMPED bill — the crew's meal (D154) above all: food was rung and
   * given away, nothing is owed and nothing was paid. Its honest end is a
   * settle at 0.00 cash, which this window used to refuse («لا شيء للدفع»,
   * disabled), stranding a closed staff meal as "closed, unpaid" for the night
   * (browser QA, 2026-10-14). A bag with nothing rung is still cancel-only.
   */
  const settlesAtZero =
    !itemsMode &&
    !closePaid &&
    remaining <= 0 &&
    bill.tenderedTotal <= 0 &&
    (bill.status === "CLOSED" || (bill.status === "OPEN" && !keepOpen)) &&
    bill.items.some((item) => isRungOnTheBill(item));
  const canPay =
    closePaid || settlesAtZero ? !pending :
    !pending &&
    remaining > 0 &&
    (itemsMode
      ? pickedCount > 0
      : !hasAmountField || (amountValid && !overCharging));

  const guard = useDiscardGuard(
    pickedCount > 0 ||
      amountText !== null ||
      hasPendingTenderReference(method, referenceText),
    onClose,
  );

  const unpaidLine = t(
    countedKey(unpaidCount, {
      one: (m) => m.paperSettle.subUnpaidOne,
      two: (m) => m.paperSettle.subUnpaidTwo,
      few: (m) => m.paperSettle.subUnpaidFew,
      many: (m) => m.paperSettle.subUnpaid,
    }),
    { count: unpaidCount },
  );
  const sub = isDone
    ? t((m) => m.paperSettle.subAllPaid)
    : bill.tenderedTotal > 0
      ? `${unpaidLine} · ${t((m) => m.paperSettle.subPaidSoFar, { amount: money(bill.tenderedTotal) })}`
      : unpaidLine;

  const itemsLabel = (count: number) =>
    t(
      countedKey(count, {
        one: (m) => m.paperSettle.labelItemsOne,
        two: (m) => m.paperSettle.labelItemsTwo,
        few: (m) => m.paperSettle.labelItemsFew,
        many: (m) => m.paperSettle.labelItems,
      }),
      { count },
    );

  const pay = async () => {
    if (!canPay) return;
    if (closePaid) {
      setPending(true);
      const closed = await send(
        {
          type: "closeInvoice",
          invoiceId: bill.id,
          payload: { expectedVersion: bill.version },
        },
        "close",
      );
      setPending(false);
      if (closed.ok && closed.invoice) setReturned(closed.invoice);
      return;
    }
    if (settlesAtZero) {
      setPending(true);
      const outcome = await runSettle(
        bill,
        { kind: "full", method: "CASH", amount: null, dinerPaymentId: undefined },
        send,
      );
      setPending(false);
      if (outcome.status === "done") setReturned(outcome.invoice);
      return;
    }
    setPending(true);
    const before = bill;
    const label = itemsMode
      ? itemsLabel(pickedCount)
      : takesPart
        ? t((m) => m.paperSettle.labelPart)
        : before.tenderedTotal > 0
          ? t((m) => m.paperSettle.labelRest)
          : t((m) => m.paperSettle.modeFull);
    const outcome = await runSettle(
      bill,
      itemsMode
        ? {
            kind: "items",
            method,
            plateIds: selectedPlateIds(rows, selection),
            partyLabel: nextPartyLabel(bill, (number) =>
              t((m) => m.paperSettle.guest, { number }),
            ),
            dinerPaymentId: claimId,
            reference: referenceText,
          }
        : {
            kind: "full",
            method,
            amount: hasAmountField && amountText !== null ? amountValue : null,
            dinerPaymentId: claimId,
            reference: referenceText,
          },
      send,
      { keepOpen: keepOpen && bill.status === "OPEN", liveDue: remaining },
    );
    setPending(false);
    if (outcome.status !== "done") return;
    setClaimId(undefined);
    const after = outcome.invoice;
    setReturned(after);
    setLast({
      amount: amountTaken(before, after),
      method,
      label,
      left: settleRemaining(after),
      change:
        after.status === "SETTLED" && method === "CASH" && after.settlementAmount !== null
          ? Math.max(0, Math.round((after.settlementAmount - after.total) * 100) / 100)
          : 0,
    });
    setSelection({});
    // D163 — a reference belongs to the tender just taken, never the next.
    setReferenceText("");
    // Back to "what is owed" — after a part payment that is the NEW remainder,
    // which is what the next tender most likely takes.
    setAmountText(null);
  };

  const writeOff = async () => {
    if (pending) return;
    setPending(true);
    const outcome = await runSettle(bill, { kind: "writeOff" }, send);
    setPending(false);
    // The panel's own written-off overlay says what happened, in the
    // destructive tone; "All paid" over a walk-out would be a lie.
    if (outcome.status === "done") onClose();
  };

  const payments = settlePayments(bill);

  const payLabel = closePaid
    ? t((m) => m.paperSettle.closePaid)
    : settlesAtZero
    ? t((m) => m.paperSettle.settleAtZero)
    : !itemsMode
    ? remaining > 0
      ? takesPart
        ? t((m) => m.paperSettle.takeNow, { amount: money(round2(amountValue)) })
        : t((m) => m.paperSettle.pay, { amount: money(remaining) })
      : t((m) => m.paperSettle.payNothing)
    : pickedCount === 0
      ? t((m) => m.paperSettle.payPickFirst)
      : payAmount === null
        ? t((m) => m.paperSettle.payPlain)
        : t((m) => m.paperSettle.pay, { amount: money(payAmount) });

  return (
    <>
      <ModalSheet
        isOpen
        size="md"
        surface="card"
        onClose={guard.requestClose}
        labelledBy={titleId}
        data-testid="paper-settle-sheet"
      >
        <div data-ui="settle-sheet" className="flex min-h-0 flex-1 flex-col">
          <div data-ui="settle-head" className="flex shrink-0 items-center">
            <DoodleBadge id={isDone ? "trophy" : "bill"} size={44} animate={false} />
            <div data-ui="settle-titles" className="flex min-w-0 flex-1 flex-col">
              <span id={titleId} data-ui="settle-title">
                {settleTableTitle(invoice, placeName, t) ??
                  t((m) => m.paperSettle.title, { name: placeName })}
              </span>
              <span data-ui="settle-sub" data-testid="paper-settle-sub">
                {sub}
              </span>
            </div>
            <div data-ui="settle-remaining" className="flex flex-col items-end">
              <span data-ui="settle-remaining-label">
                {t((m) => m.paperSettle.remaining)}
              </span>
              <span
                data-ui="settle-remaining-figure"
                data-testid="paper-settle-remaining"
              >
                {money(remaining)}
              </span>
            </div>
            <button
              type="button"
              data-ui="settle-close"
              aria-label={t((m) => m.common.close)}
              title={t((m) => m.common.close)}
              onClick={guard.requestClose}
            >
              <X size={15} style={{ color: ICON_INK }} strokeWidth={1.75} aria-hidden="true" />
            </button>
          </div>

          {last ? (
            <div
              data-ui="settle-last"
              role="status"
              data-testid="paper-settle-last"
              className="shrink-0"
            >
              {[
                t((m) => m.paperSettle.lastPaid, {
                  amount: money(last.amount),
                  method: settlementMethodLabel(t, last.method).toLowerCase(),
                }),
                last.label,
                last.left > 0
                  ? t((m) => m.paperSettle.lastLeft, { amount: money(last.left) })
                  : null,
                last.change > 0
                  ? `${t((m) => m.pos.changeDue)} ${money(last.change)}`
                  : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </div>
          ) : null}

          {isDone ? (
            <div
              data-ui="settle-done"
              data-testid="paper-settle-done"
              className="flex shrink-0 flex-col items-center text-center"
            >
              <span data-ui="settle-done-title">
                {t((m) => m.paperSettle.doneTitle)}
              </span>
              <span data-ui="settle-done-body">
                {t((m) => m.paperSettle.doneBody, { name: placeName })}
              </span>
              <button
                type="button"
                data-ui="settle-done-button"
                data-testid="paper-settle-done-button"
                onClick={onClose}
              >
                {t((m) => m.paperSettle.done)}
              </button>
            </div>
          ) : (
            <>
              {canPayByItem ? (
                <div data-ui="settle-modes-wrap" className="shrink-0">
                  <div
                    data-ui="settle-segmented"
                    data-kind="mode"
                    role="group"
                    aria-label={t((m) => m.paperSettle.modesLabel)}
                    className="flex"
                  >
                    {(
                      [
                        ["full", Receipt, t((m) => m.paperSettle.modeFull)],
                        ["items", ListChecks, t((m) => m.paperSettle.modeItems)],
                      ] as const
                    ).map(([id, Icon, label]) => (
                      <button
                        key={id}
                        type="button"
                        data-ui="settle-segment"
                        data-active={mode === id ? "" : undefined}
                        aria-pressed={mode === id}
                        data-testid={`paper-settle-mode-${id}`}
                        disabled={pending}
                        onClick={() => setMode(id)}
                      >
                        <Icon size={15} style={{ color: ICON_INK }} strokeWidth={1.9} aria-hidden="true" />
                        <span>{label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}

              <div
                data-ui="settle-body"
                data-mode={itemsMode ? "items" : "full"}
                className="min-h-0 flex-1 overflow-y-auto"
              >
                {itemsMode ? (
                  <>
                    <div data-ui="settle-items-bar" className="flex items-center">
                      <span data-ui="settle-items-info" data-testid="paper-settle-selected">
                        {pickedCount > 0
                          ? t(
                              countedKey(pickedCount, {
                                one: (m) => m.paperSettle.itemsSelectedOne,
                                two: (m) => m.paperSettle.itemsSelectedTwo,
                                few: (m) => m.paperSettle.itemsSelectedFew,
                                many: (m) => m.paperSettle.itemsSelected,
                              }),
                              {
                                count: pickedCount,
                                amount:
                                  pickedAmount === null ? "—" : money(pickedAmount),
                              },
                            )
                          : t((m) => m.paperSettle.itemsPick)}
                      </span>
                      <button
                        type="button"
                        data-ui="settle-link"
                        data-tone="accent"
                        data-testid="paper-settle-select-all"
                        disabled={pending}
                        onClick={() =>
                          setSelection(
                            Object.fromEntries(
                              rows.map((row) => [row.key, row.plateIds.length]),
                            ),
                          )
                        }
                      >
                        {t((m) => m.paperSettle.selectAll)}
                      </button>
                      <button
                        type="button"
                        data-ui="settle-link"
                        data-testid="paper-settle-clear"
                        disabled={pending}
                        onClick={() => setSelection({})}
                      >
                        {t((m) => m.paperSettle.clear)}
                      </button>
                    </div>
                    {rows.map((row) => {
                      const left = row.plateIds.length;
                      const picked = Math.min(selection[row.key] ?? 0, left);
                      const name = localName({
                        nameAr: row.group.plateNameAr,
                        nameEn: row.group.plateNameEn,
                      });
                      const detail = [
                        localName({
                          nameAr: row.group.addonLabel ?? "",
                          nameEn: row.group.addonLabelEn,
                        }),
                        localName({
                          nameAr: row.group.comboLabel ?? "",
                          nameEn: row.group.comboLabelEn,
                        }),
                        localName({
                          nameAr: row.group.substitutionLabel ?? "",
                          nameEn: row.group.substitutionLabelEn,
                        }),
                      ]
                        .filter(Boolean)
                        .join(" · ");
                      const toggle = () =>
                        setSelection((current) => ({
                          ...current,
                          [row.key]: picked > 0 ? 0 : left,
                        }));
                      return (
                        <div
                          key={row.key}
                          data-ui="settle-row"
                          data-selected={picked > 0 ? "" : undefined}
                          data-testid={`paper-settle-row-${row.key}`}
                          className="flex items-center"
                          onClick={pending ? undefined : toggle}
                        >
                          <button
                            type="button"
                            role="checkbox"
                            aria-checked={
                              picked === 0 ? false : picked === left ? true : "mixed"
                            }
                            aria-label={name}
                            data-ui="settle-check"
                            disabled={pending}
                            onClick={(event) => {
                              event.stopPropagation();
                              toggle();
                            }}
                          >
                            {picked > 0 ? (
                              <Check size={11} color="#fff" strokeWidth={1.9} aria-hidden="true" />
                            ) : null}
                          </button>
                          <span data-ui="settle-row-text" className="flex min-w-0 flex-1 flex-col">
                            <span data-ui="settle-row-name">
                              {left}× <bdi>{name}</bdi>
                            </span>
                            {detail ? (
                              <span data-ui="settle-row-detail">{detail}</span>
                            ) : null}
                          </span>
                          {left > 1 ? (
                            <div data-ui="settle-stepper" className="flex items-center">
                              <button
                                type="button"
                                data-ui="settle-step"
                                aria-label={t((m) => m.paperSettle.fewer, { name })}
                                data-testid={`paper-settle-less-${row.key}`}
                                disabled={pending || picked === 0}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setSelection((current) => ({
                                    ...current,
                                    [row.key]: Math.max(0, picked - 1),
                                  }));
                                }}
                              >
                                −
                              </button>
                              <span data-ui="settle-step-count" dir="ltr">
                                {picked} / {left}
                              </span>
                              <button
                                type="button"
                                data-ui="settle-step"
                                aria-label={t((m) => m.paperSettle.more, { name })}
                                data-testid={`paper-settle-more-${row.key}`}
                                disabled={pending || picked === left}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setSelection((current) => ({
                                    ...current,
                                    [row.key]: Math.min(left, picked + 1),
                                  }));
                                }}
                              >
                                +
                              </button>
                            </div>
                          ) : null}
                          <span data-ui="settle-row-price" className="flex flex-col">
                            <span data-ui="settle-row-total">
                              {row.total === null ? "—" : money(row.total)}
                            </span>
                            <span data-ui="settle-row-each">
                              {left > 1 && row.each !== null
                                ? t((m) => m.paperSettle.each, { amount: money(row.each as number) })
                                : ""}
                            </span>
                          </span>
                        </div>
                      );
                    })}
                    {partPaid ? (
                      <div data-ui="settle-note">
                        {t((m) => m.paperSettle.partPaidNote)}
                      </div>
                    ) : null}
                  </>
                ) : (
                  <div data-ui="settle-full" data-testid="paper-settle-full">
                    <span>
                      {t((m) => m.paperSettle.fullInfo, { amount: money(remaining) })}
                    </span>
                    {hasAmountField ? (
                      <label data-ui="settle-cash" className="flex flex-col">
                        <span data-ui="settle-cash-label">
                          {method === "CASH"
                            ? t((m) => m.paperSettle.cashReceived)
                            : t((m) => m.paperSettle.amountLabel)}
                        </span>
                        <NumberInput
                          name="settleAmount"
                          allowDecimal
                          min={0}
                          dir="ltr"
                          value={amountText ?? String(remaining)}
                          hasError={!amountValid || overCharging}
                          onChange={(event) => setAmountText(event.target.value)}
                          data-testid="paper-settle-amount"
                        />
                        {overCharging ? (
                          <span data-ui="settle-cash-error" role="alert">
                            {t((m) => m.pos.partialOverChargeHint)}
                          </span>
                        ) : takesPart ? (
                          /* What a part payment LEAVES — said before the tap,
                             so nobody mistakes "take 200 now" for a settle. */
                          <span data-ui="settle-cash-left" data-testid="paper-settle-leaves">
                            {t((m) => m.pos.partialLeaves, {
                              amount: money(round2(remaining - amountValue)),
                            })}
                          </span>
                        ) : change > 0 ? (
                          <span data-ui="settle-cash-change" data-testid="paper-settle-change">
                            {t((m) => m.pos.changeDue)} {money(change)}
                          </span>
                        ) : null}
                      </label>
                    ) : null}
                    {method === "AGGREGATOR" && aggregator ? (
                      <p data-ui="settle-hint" data-testid="paper-settle-platform-hint">
                        {t((m) => m.pos.aggregatorSettleHint, {
                          name: aggregator.name,
                          rate: String(aggregator.commissionPercent),
                        })}{" "}
                        {/* T15 — what the rate is taken on, off the bill's snapshot. */}
                        {t((m) => m.pos.aggregatorSettleHintBase, {
                          base: commissionBaseLabel(aggregator.commissionBase ?? "TOTAL", t),
                        })}
                      </p>
                    ) : null}
                  </div>
                )}
              </div>

              <div data-ui="settle-foot" className="flex shrink-0 flex-col">
                {warning ? (
                  <DrawerWarning
                    message={warning}
                    actionLabel={warningActionLabel ?? ""}
                    onOpenDrawer={warningActionLabel ? onOpenDrawer : undefined}
                    testId="paper-settle-drawer-warning"
                  />
                ) : null}
                {writeOffAsked ? (
                  <div data-ui="settle-write-off" data-testid="paper-settle-write-off" className="flex flex-col">
                    <span data-ui="settle-write-off-title">
                      {t((m) => m.pos.writeOffTitle)}
                    </span>
                    <span data-ui="settle-write-off-body">
                      {t((m) => m.pos.writeOffBody)}
                    </span>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        data-ui="settle-secondary"
                        disabled={pending}
                        onClick={() => setWriteOffAsked(false)}
                      >
                        {t((m) => m.common.cancel)}
                      </button>
                      <button
                        type="button"
                        data-ui="settle-danger"
                        data-testid="paper-settle-write-off-confirm"
                        disabled={pending}
                        onClick={() => void writeOff()}
                      >
                        {t((m) => m.pos.writeOffConfirm)}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div
                      data-ui="settle-segmented"
                      data-kind="method"
                      role="group"
                      aria-label={t((m) => m.pos.settlementMethod)}
                      className="flex"
                    >
                      {options.map((option) => {
                        const Icon = METHOD_ICONS[option.method];
                        return (
                          <button
                            key={option.method}
                            type="button"
                            data-ui="settle-segment"
                            data-active={method === option.method ? "" : undefined}
                            data-locked={option.enabled ? undefined : ""}
                            aria-pressed={method === option.method}
                            aria-disabled={option.enabled ? undefined : true}
                            title={
                              option.enabled
                                ? undefined
                                : t((m) => m.paperSettle.instapayLocked)
                            }
                            data-testid={`paper-settle-method-${option.method}`}
                            disabled={pending}
                            onClick={() => {
                              if (!option.enabled) {
                                setInstapayHint(true);
                                return;
                              }
                              setInstapayHint(false);
                              setChosen(option.method);
                            }}
                          >
                            <Icon size={15} style={{ color: ICON_INK }} strokeWidth={1.9} aria-hidden="true" />
                            <span>
                              {option.method === "AGGREGATOR" && aggregator
                                ? aggregator.name
                                : settlementMethodLabel(t, option.method)}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    {/* D163 — only while CARD or INSTAPAY is chosen; never
                        blocks Pay, whatever it holds. Not on the zero
                        settle or the paid-open close: no tender there. */}
                    {!closePaid && !settlesAtZero ? (
                      <div data-ui="settle-reference">
                        <TenderReferenceField
                          method={method}
                          value={referenceText}
                          onChange={setReferenceText}
                          disabled={pending}
                          testId="paper-settle-reference"
                        />
                      </div>
                    ) : null}
                    {instapayHint ? (
                      <span
                        data-ui="settle-method-hint"
                        role="status"
                        data-testid="paper-settle-instapay-hint"
                      >
                        {t((m) => m.paperSettle.instapayLocked)}
                      </span>
                    ) : null}
                    <button
                      type="button"
                      data-ui="settle-pay"
                      data-ready={canPay ? "" : undefined}
                      aria-disabled={canPay ? undefined : true}
                      aria-busy={pending || undefined}
                      data-testid="paper-settle-pay"
                      onClick={() => void pay()}
                    >
                      {payLabel}
                    </button>
                    <span data-ui="settle-foot-note">
                      {bill.status === "OPEN"
                        ? paidOpen && keepOpen
                          ? t((m) => m.paperSettle.footPaidWaiting)
                          : keepOpen
                          ? t((m) => m.paperSettle.footKeepsOpen)
                          : t((m) => m.paperSettle.footOpen)
                        : t((m) => m.paperSettle.footClosed)}
                    </span>
                    {bill.status === "OPEN" && keepOpen && keepOpenNote ? keepOpenNote : null}
                    {canWriteOff && !itemsMode && bill.tenderedTotal === 0 ? (
                      <button
                        type="button"
                        data-ui="settle-link"
                        data-tone="danger"
                        data-testid="paper-settle-write-off-open"
                        disabled={pending}
                        onClick={() => setWriteOffAsked(true)}
                      >
                        {t((m) => m.paperSettle.writeOffOpen)}
                      </button>
                    ) : null}
                  </>
                )}
              </div>
            </>
          )}

          {payments.length > 0 ? (
            <div
              data-ui="settle-payments"
              data-testid="paper-settle-payments"
              className="flex shrink-0 flex-col"
            >
              <span data-ui="settle-payments-title">
                {t((m) => m.paperSettle.payments)}
              </span>
              {payments.map((payment, index) => (
                <div
                  key={`${payment.method}-${index}`}
                  data-ui="settle-payment"
                  className="flex items-center"
                >
                  <span data-ui="settle-payment-amount">{money(payment.amount)}</span>
                  <span data-ui="settle-payment-label" className="min-w-0 flex-1">
                    {settlementMethodLabel(t, payment.method)}
                    {/* D163 — the reference(s) typed on this method's tenders. */}
                    {payment.references && payment.references.length > 0 ? (
                      <>
                        {" · "}
                        <bdi
                          dir="ltr"
                          data-testid={`paper-settle-payment-ref-${payment.method}`}
                        >
                          {payment.references.join(" · ")}
                        </bdi>
                      </>
                    ) : null}
                  </span>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </ModalSheet>
      <UnsavedChangesPrompt
        isOpen={guard.asking}
        onDiscard={guard.discard}
        onKeepEditing={guard.keepEditing}
        testId="paper-settle-discard-prompt"
      />
    </>
  );
}

/** A by-item selection holding the given plates: per row, how many of them it has. */
function selectionForPlates(
  rows: ReturnType<typeof settleRows>,
  plateIds: readonly string[],
): SettleSelection {
  const wanted = new Set(plateIds);
  const out: Record<string, number> = {};
  for (const row of rows) {
    const count = row.plateIds.filter((id) => wanted.has(id)).length;
    if (count > 0) out[row.key] = count;
  }
  return out;
}
